import type { VisualDesignerSurvey } from '../services/visualDesignerSurveys.service';

export type QuoteLikeVersion = {
  id: string;
  documentId: string;
  versionNumber: number;
  isActive: boolean;
  isTransmitted: boolean;
  overrideUnlocked: boolean;
  visualSurveyId: string | null;
  lifecycleRef: string;
  kind: 'Estimate' | 'Quotation';
};

export type VersionPairingRow = {
  key: string;
  surveyId: string | null;
  surveyLabel: string | null;
  versionId: string | null;
  documentId: string | null;
  versionLabel: string | null;
  versionShortLabel: string | null;
  locked: boolean;
};

export function documentVersionLabel(version: QuoteLikeVersion) {
  const kind = version.kind === 'Quotation' ? 'Quote' : 'Estimate';
  const ref = String(version.lifecycleRef || '').trim() || 'Document';
  return `${ref} · ${kind} · Version ${version.versionNumber}`;
}

export function documentVersionShortLabel(version: QuoteLikeVersion) {
  const kind = version.kind === 'Quotation' ? 'Quote' : 'Est';
  return `${kind} v${version.versionNumber}`;
}

export function buildVersionPairingRows(
  surveys: VisualDesignerSurvey[],
  versions: QuoteLikeVersion[]
): VersionPairingRow[] {
  const versionById = new Map(versions.map((version) => [version.id, version]));
  const usedVersionIds = new Set<string>();
  const usedSurveyIds = new Set<string>();
  const rows: VersionPairingRow[] = [];

  const orderedSurveys = [...surveys].sort((a, b) => a.sortOrder - b.sortOrder || a.createdAt.localeCompare(b.createdAt));

  for (const survey of orderedSurveys) {
    const bound = survey.documentVersionId ? versionById.get(survey.documentVersionId) : undefined;
    const fallback = !bound
      ? versions.find((version) => version.visualSurveyId === survey.id && !usedVersionIds.has(version.id))
      : undefined;
    const version = bound || fallback || null;
    if (version) usedVersionIds.add(version.id);
    usedSurveyIds.add(survey.id);
    rows.push(toRow(survey, version));
  }

  const leftover = [...versions]
    .filter((version) => !usedVersionIds.has(version.id))
    .sort((a, b) => {
      if (a.kind !== b.kind) return a.kind === 'Estimate' ? -1 : 1;
      if (a.lifecycleRef !== b.lifecycleRef) return a.lifecycleRef.localeCompare(b.lifecycleRef);
      return a.versionNumber - b.versionNumber;
    });

  for (const version of leftover) {
    rows.push(toRow(null, version));
  }

  return rows;
}

export function pairingLabelForVersion(
  versionId: string | null | undefined,
  rows: VersionPairingRow[]
) {
  if (!versionId) return null;
  return rows.find((row) => row.versionId === versionId)?.surveyLabel || null;
}

export function pairingLabelForSurvey(
  surveyId: string | null | undefined,
  rows: VersionPairingRow[]
) {
  if (!surveyId) return null;
  return rows.find((row) => row.surveyId === surveyId)?.versionShortLabel || null;
}

function toRow(survey: VisualDesignerSurvey | null, version: QuoteLikeVersion | null): VersionPairingRow {
  return {
    key: survey?.id || version?.id || 'empty',
    surveyId: survey?.id || null,
    surveyLabel: survey?.label || null,
    versionId: version?.id || null,
    documentId: version?.documentId || null,
    versionLabel: version ? documentVersionLabel(version) : null,
    versionShortLabel: version ? documentVersionShortLabel(version) : null,
    locked: !!(version?.isTransmitted && !version.overrideUnlocked),
  };
}
